import React from 'react';
import ProductCard from './ProductCard';
import { ProductGridSkeleton } from './ProductSkeleton';
import EmptyState from '../ui/EmptyState';
import { ChevronLeftIcon, ChevronRightIcon } from '../icons/Icons';

/**
 * ============================================
 * Grille de Produits - Style Modatex
 * ============================================
 * Disposition de grille uniforme et élégante
 */

const ProductGrid = ({ 
    products, 
    isLoading, 
    searchTerm, 
    selectedCategory, 
    onProductClick, 
    onResetFilters,
    pagination,
    currentPage,
    onPageChange
}) => {
    // Show loading skeleton
    if (isLoading) {
        return (
            <section className="px-6 py-8">
                <ProductGridSkeleton count={10} />
            </section>
        );
    }

    // Show empty state if no products
    if (!products || products.length === 0) {
        return (
            <section className="px-6 py-8">
                <EmptyState 
                    searchTerm={searchTerm}
                    selectedCategory={selectedCategory}
                    onReset={onResetFilters}
                />
            </section>
        );
    }

    const totalPages = pagination?.pages || 1;
    const totalProducts = pagination?.total || products.length;

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);
            
            // Calculate range around current page
            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);
            
            // Adjust range if near beginning or end
            if (currentPage <= 3) {
                end = 4;
            } else if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
            }
            
            // Add ellipsis and pages
            if (start > 2) {
                pages.push('...');
            }
            
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            if (end < totalPages - 1) {
                pages.push('...');
            }
            
            // Always show last page
            pages.push(totalPages);
        }
        
        return pages;
    };

    return (
        <section className="w-full py-8 bg-gray-100">
            <div className="w-full px-6">
                {/* Results count */}
                <div className="mb-4 text-sm text-gray-600">
                    Affichage de {products.length} sur {totalProducts} produits
                </div>

                {/* Full Width Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                    {products.map((product) => (
                        <ProductCard
                            key={product._id || product.id}
                            product={product}
                            onClick={onProductClick}
                        />
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-12 flex items-center justify-center gap-2">
                        {/* Previous Button */}
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                                currentPage === 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                            Précédent
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                            {getPageNumbers().map((page, index) => (
                                page === '...' ? (
                                    <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                                        ...
                                    </span>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => onPageChange(page)}
                                        className={`min-w-[40px] h-10 flex items-center justify-center text-sm font-medium transition-colors ${
                                            currentPage === page
                                                ? 'bg-gray-900 text-white'
                                                : 'text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                )
                            ))}
                        </div>

                        {/* Next Button */}
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors ${
                                currentPage === totalPages
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Suivant
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default ProductGrid;
